// Pure form-state logic for the budget editor. Income schedules and category
// breakdown lines share the same draft shape and the same draft→ScheduleItem
// conversion; keeping it here (free of Svelte/DOM) lets the parsing, totalling,
// and change-detection rules be unit-tested directly.

import { alignFortnightlyPayDate, alignFourWeeklyPayDate, formatDate, isWithinRange } from './budget';
import { scheduleItemTotal, type ScheduleItem } from './schedule';
import { getPayCycleRange } from './month';
import { parseDateOnly } from './format';
import type { CategoryLine, IncomeSchedule } from './db';

export type Frequency = 'monthly' | 'fortnightly' | 'four-weekly' | 'weekly' | 'one-off';

// The income-schedule and category-line drafts differ only in that a schedule
// carries a category; the schedule fields below are common to both.
interface ScheduleDraftFields {
	amount: string;
	frequency: Frequency;
	monthlyDay: string;
	fortnightlyPayDate: string;
	fourWeeklyPayDate: string;
	oneOffDate: string;
	weeklyDay: string;
}

export interface IncomeScheduleDraft extends ScheduleDraftFields {
	id: string;
	name: string;
	category: string;
}

export interface CategoryLineDraft extends ScheduleDraftFields {
	id: string;
	name: string;
}

export type Range = { start: Date; end: Date };

/**
 * Parse a keystroke amount into a number, or null when blank/non-numeric.
 * Numbers pass through (NaN/Infinity become null).
 */
export function parseAmount(value: string | number | null | undefined): number | null {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}
	if (!value.trim()) return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

/** Normalise an amount field on blur: "" for unparseable, else fixed 2dp. */
export function formatAmountOnBlur(value: string | number | null | undefined): string {
	const parsed = parseAmount(value);
	if (parsed === null) return '';
	return parsed.toFixed(2);
}

/**
 * Convert a draft (income schedule or category line) into the shared
 * ScheduleItem the recurrence math consumes. Only the field its frequency
 * requires is populated.
 */
export function draftToScheduleItem(draft: ScheduleDraftFields): ScheduleItem {
	const amount = parseAmount(draft.amount) ?? 0;
	const item: ScheduleItem = { amount, frequency: draft.frequency };
	if (draft.frequency === 'monthly') {
		item.monthlyDay = Number(draft.monthlyDay) || 15;
	} else if (draft.frequency === 'fortnightly') {
		item.fortnightlyPayDate = draft.fortnightlyPayDate;
	} else if (draft.frequency === 'four-weekly') {
		item.fourWeeklyPayDate = draft.fourWeeklyPayDate;
	} else if (draft.frequency === 'weekly') {
		const weekday = Number(draft.weeklyDay);
		if (Number.isFinite(weekday)) item.weeklyDay = weekday;
	} else if (draft.frequency === 'one-off') {
		item.oneOffDate = draft.oneOffDate;
	}
	return item;
}

/** Total a set of drafts over the pay cycle for `yearMonth`. */
export function draftsTotal(drafts: ScheduleDraftFields[], yearMonth: string): number {
	const { start, end } = getPayCycleRange(yearMonth);
	return drafts.reduce(
		(sum, draft) => sum + scheduleItemTotal(draftToScheduleItem(draft), start, end),
		0
	);
}

/**
 * Income drafts → persisted IncomeSchedule[], dropping rows with no category or
 * a zero amount. Mirrors the on-save shape but without validation (used for the
 * live breakdown panel).
 */
export function draftsToSchedules(drafts: IncomeScheduleDraft[]): IncomeSchedule[] {
	return drafts
		.filter((d) => d.category.trim() && (parseAmount(d.amount) ?? 0) !== 0)
		.map((d) => ({
			name: d.name.trim() || undefined,
			category: d.category.trim(),
			amount: parseAmount(d.amount) ?? 0,
			frequency: d.frequency,
			monthlyDay: d.frequency === 'monthly' ? Number(d.monthlyDay) || 15 : undefined,
			fortnightlyPayDate: d.frequency === 'fortnightly' ? d.fortnightlyPayDate : undefined,
			fourWeeklyPayDate: d.frequency === 'four-weekly' ? d.fourWeeklyPayDate : undefined,
			oneOffDate: d.frequency === 'one-off' ? d.oneOffDate : undefined,
			weeklyDay: d.frequency === 'weekly' ? Number(d.weeklyDay) : undefined,
		}));
}

/** A fresh income-schedule draft, anchored to the cycle start. `id` is caller-supplied. */
export function newScheduleDraft(id: string, range: Range): IncomeScheduleDraft {
	return {
		id,
		name: '',
		category: '',
		amount: '',
		frequency: 'monthly',
		monthlyDay: '15',
		fortnightlyPayDate: formatDate(range.start),
		fourWeeklyPayDate: formatDate(range.start),
		oneOffDate: formatDate(range.start),
		weeklyDay: '1',
	};
}

/** A fresh category-line draft, anchored to the cycle start. `id` is caller-supplied. */
export function newCategoryLineDraft(id: string, range: Range): CategoryLineDraft {
	return {
		id,
		name: '',
		amount: '',
		frequency: 'weekly',
		monthlyDay: '15',
		fortnightlyPayDate: formatDate(range.start),
		fourWeeklyPayDate: formatDate(range.start),
		oneOffDate: formatDate(range.start),
		weeklyDay: '6',
	};
}

/**
 * True when a saved line/schedule should survive a clone into `range`:
 * recurring frequencies always clone; a one-off belongs to a single cycle, so
 * it clones only if its date lands inside the new cycle.
 */
export function survivesClone(
	item: { frequency: Frequency; oneOffDate?: string },
	range: Range
): boolean {
	if (item.frequency !== 'one-off') return true;
	if (!item.oneOffDate) return false;
	const date = parseDateOnly(item.oneOffDate);
	return Number.isFinite(date.getTime()) && isWithinRange(date, range.start, range.end);
}

/** Hydrate a saved IncomeSchedule into an editable draft. */
export function draftFromSchedule(schedule: IncomeSchedule, id: string, range: Range): IncomeScheduleDraft {
	return {
		id,
		name: schedule.name ?? '',
		category: schedule.category ?? '',
		amount: formatAmountOnBlur(schedule.amount?.toString() ?? ''),
		frequency: schedule.frequency,
		monthlyDay: schedule.monthlyDay?.toString() ?? '15',
		fortnightlyPayDate: alignFortnightlyPayDate(
			schedule.fortnightlyPayDate ?? formatDate(range.start),
			range
		),
		fourWeeklyPayDate: alignFourWeeklyPayDate(
			schedule.fourWeeklyPayDate ?? formatDate(range.start),
			range
		),
		oneOffDate: schedule.oneOffDate ?? formatDate(range.start),
		weeklyDay: schedule.weeklyDay?.toString() ?? '1',
	};
}

/** Hydrate a saved CategoryLine into an editable draft. */
export function lineDraftFromCategoryLine(line: CategoryLine, id: string, range: Range): CategoryLineDraft {
	return {
		id,
		name: line.name ?? '',
		amount: formatAmountOnBlur(line.amount?.toString() ?? ''),
		frequency: line.frequency,
		monthlyDay: line.monthlyDay?.toString() ?? '15',
		fortnightlyPayDate: alignFortnightlyPayDate(
			line.fortnightlyPayDate ?? formatDate(range.start),
			range
		),
		fourWeeklyPayDate: alignFourWeeklyPayDate(
			line.fourWeeklyPayDate ?? formatDate(range.start),
			range
		),
		oneOffDate: line.oneOffDate ?? formatDate(range.start),
		weeklyDay: line.weeklyDay?.toString() ?? '6',
	};
}

/**
 * Stable string fingerprint of the editable budget state for unsaved-change
 * detection. Amounts are normalised and blank entries dropped, schedules and
 * lines are sorted, so reordering or whitespace never reads as a change.
 */
export function serializeBudgetDraft(
	amounts: Record<string, string>,
	schedules: IncomeScheduleDraft[],
	lines: Record<string, CategoryLineDraft[]>
): string {
	const normalizedAmounts = Object.keys(amounts)
		.sort()
		.map((name) => [name, formatAmountOnBlur(amounts[name] ?? '')])
		.filter(([, value]) => value !== '');

	const normalizedSchedules = schedules.map((schedule) => ({
		name: schedule.name.trim(),
		category: schedule.category.trim(),
		amount: formatAmountOnBlur(schedule.amount),
		frequency: schedule.frequency,
		monthlyDay: schedule.monthlyDay.trim(),
		fortnightlyPayDate: schedule.fortnightlyPayDate,
		fourWeeklyPayDate: schedule.fourWeeklyPayDate,
		oneOffDate: schedule.oneOffDate,
		weeklyDay: schedule.weeklyDay.trim(),
	}));

	normalizedSchedules.sort((a, b) =>
		`${a.category}|${a.name}|${a.frequency}`.localeCompare(`${b.category}|${b.name}|${b.frequency}`)
	);

	const normalizedLines = Object.keys(lines)
		.sort()
		.flatMap((category) =>
			lines[category].map((line) => ({
				category,
				name: line.name.trim(),
				amount: formatAmountOnBlur(line.amount),
				frequency: line.frequency,
				monthlyDay: line.monthlyDay.trim(),
				fortnightlyPayDate: line.fortnightlyPayDate,
				fourWeeklyPayDate: line.fourWeeklyPayDate,
				oneOffDate: line.oneOffDate,
				weeklyDay: line.weeklyDay.trim(),
			}))
		);

	return JSON.stringify({
		amounts: normalizedAmounts,
		schedules: normalizedSchedules,
		lines: normalizedLines,
	});
}
